'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeftRight } from 'lucide-react';

import styles from '@/app/[locale]/ai-model/ai-models.module.css';
import ModelMark from '@/components/ai-models/ModelMark';
import {
  buildComparisonPath,
  buildRecommendations,
  calculateMonthlyCost,
  compareModels,
  formatTokenCount,
  formatUsdPrice,
  localizedModelPath,
  sameComparisonValue,
  type AiModel,
  type ComparisonRow,
} from '@/libs/ai-models';
import { getAiModelsCopy } from '@/libs/ai-models-i18n';

export type ModelCompareOption = Pick<AiModel, 'slug' | 'name' | 'shortName'>;

function clampTokens(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(10_000_000_000, Math.max(0, Math.round(value)));
}

export default function ModelCompareWorkbench({
  firstModel,
  secondModel,
  modelOptions,
  locale,
}: {
  firstModel: AiModel;
  secondModel: AiModel;
  modelOptions: ModelCompareOption[];
  locale: string;
}) {
  const copy = getAiModelsCopy(locale);
  const router = useRouter();
  const [visualSlugs, setVisualSlugs] = useState<[string, string]>([firstModel.slug, secondModel.slug]);
  const [differencesOnly, setDifferencesOnly] = useState(false);
  const [inputTokens, setInputTokens] = useState(1_000_000);
  const [outputTokens, setOutputTokens] = useState(200_000);
  const [cachedInputTokens, setCachedInputTokens] = useState(0);

  const first = visualSlugs[0] === secondModel.slug ? secondModel : firstModel;
  const second = visualSlugs[1] === firstModel.slug ? firstModel : secondModel;
  const rows = useMemo(() => compareModels(first, second), [first, second]);
  const visibleRows = differencesOnly ? rows.filter((row) => !sameComparisonValue(row)) : rows;
  const recommendations = useMemo(
    () => buildRecommendations(first, second, inputTokens, outputTokens, cachedInputTokens),
    [first, second, inputTokens, outputTokens, cachedInputTokens],
  );
  const firstCost = calculateMonthlyCost({ model: first, inputTokens, outputTokens, cachedInputTokens });
  const secondCost = calculateMonthlyCost({ model: second, inputTokens, outputTokens, cachedInputTokens });

  const navigatePair = (nextFirst: string, nextSecond: string) => {
    if (nextFirst === nextSecond) return;
    router.push(buildComparisonPath(locale, nextFirst, nextSecond));
  };

  const updateInputTokens = (value: number) => {
    const nextInputTokens = clampTokens(value);
    setInputTokens(nextInputTokens);
    setCachedInputTokens((current) => Math.min(current, nextInputTokens));
  };

  const renderValue = (row: ComparisonRow, value: ComparisonRow['first']) => {
    if (value === null) return copy.detail.unknown;
    if (Array.isArray(value)) return value.map((item) => copy.modalityLabels[item as keyof typeof copy.modalityLabels] || item).join(', ');
    if (typeof value === 'number') {
      return row.key.includes('Price') ? formatUsdPrice(value, locale) : formatTokenCount(value, locale);
    }
    if (value === 'supported' || value === 'unsupported' || value === 'unknown') return copy.triStateLabels[value];
    return value;
  };

  const winnerLabel = (winner: string) => {
    if (winner === 'tie') return copy.comparePage.tie;
    if (winner === 'insufficient_data') return copy.comparePage.insufficient;
    return modelOptions.find((model) => model.slug === winner)?.shortName || winner;
  };

  return (
    <>
      <section className={styles.compareSelector} aria-label={copy.compare}>
        <label>
          <span>{copy.comparePage.modelA}</span>
          <select value={first.slug} onChange={(event) => navigatePair(event.target.value, second.slug)}>
            {modelOptions.map((model) => <option value={model.slug} disabled={model.slug === second.slug} key={model.slug}>{model.name}</option>)}
          </select>
        </label>
        <button
          type="button"
          className={styles.swapButton}
          onClick={() => setVisualSlugs(([a, b]) => [b, a])}
          aria-label={copy.comparePage.swap}
          title={copy.comparePage.swap}
        >
          <ArrowLeftRight aria-hidden="true" />
        </button>
        <label>
          <span>{copy.comparePage.modelB}</span>
          <select value={second.slug} onChange={(event) => navigatePair(first.slug, event.target.value)}>
            {modelOptions.map((model) => <option value={model.slug} disabled={model.slug === first.slug} key={model.slug}>{model.name}</option>)}
          </select>
        </label>
      </section>

      <section className={styles.recommendationSection} aria-labelledby="model-decision-summary">
        <div className={styles.sectionHeading}>
          <span className={styles.sectionEyebrow}>DECISION SUMMARY</span>
          <h2 id="model-decision-summary">{copy.comparePage.decisionTitle}</h2>
          <p>{copy.comparePage.decisionIntro}</p>
        </div>
        <div className={styles.recommendationGrid}>
          {recommendations.map((recommendation) => (
            <article className={recommendation.winner === 'insufficient_data' ? styles.recommendationMuted : styles.recommendationCard} key={recommendation.category}>
              <h3>{copy.comparePage.recommendations[recommendation.category]}</h3>
              <strong>{winnerLabel(recommendation.winner)}</strong>
              <details>
                <summary>{copy.comparePage.evidence}</summary>
                <code>{recommendation.reasonCode}</code>
                <p>{recommendation.evidence}</p>
              </details>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.comparisonSection} aria-labelledby="full-model-comparison">
        <div className={styles.comparisonToolbar}>
          <div>
            <span className={styles.sectionEyebrow}>MODEL ATTRIBUTES</span>
            <h2 id="full-model-comparison">{copy.comparePage.fullComparison}</h2>
            <p>{copy.comparePage.sourceScope}: {copy.detail.endpointScope}</p>
          </div>
          <label className={styles.differenceToggle}>
            <input type="checkbox" checked={differencesOnly} onChange={(event) => setDifferencesOnly(event.target.checked)} />
            <span>{copy.comparePage.showDifferences}</span>
          </label>
        </div>

        <div className={styles.comparisonTableWrap}>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{copy.comparePage.attribute}</th>
                <th><span className={styles.tableModelHeading}><ModelMark model={first} compact /> {first.shortName}</span></th>
                <th><span className={styles.tableModelHeading}><ModelMark model={second} compact /> {second.shortName}</span></th>
                <th>{copy.comparePage.winner}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.key}>
                  <th scope="row">
                    {copy.comparisonRowLabels[row.key] || row.key}
                    <code>{row.reasonCode}</code>
                    <small>{row.sourceScope === 'model' ? copy.detail.modelScope : copy.detail.endpointScope}</small>
                  </th>
                  <td className={row.winner === first.slug ? styles.winningCell : ''}>{renderValue(row, row.first)}</td>
                  <td className={row.winner === second.slug ? styles.winningCell : ''}>{renderValue(row, row.second)}</td>
                  <td>{winnerLabel(row.winner)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.costSection} aria-labelledby="monthly-cost-calculator">
        <div className={styles.costIntro}>
          <span className={styles.sectionEyebrow}>USAGE ESTIMATOR</span>
          <h2 id="monthly-cost-calculator">{copy.comparePage.costTitle}</h2>
          <p>{copy.comparePage.costIntro}</p>
          <small>{copy.comparePage.costDisclaimer}</small>
        </div>
        <div className={styles.costCalculator}>
          <div className={styles.costInputs}>
            <label><span>{copy.comparePage.inputTokens}</span><input type="number" min="0" max="10000000000" step="100000" value={inputTokens} onChange={(event) => updateInputTokens(event.target.valueAsNumber)} /></label>
            <label><span>{copy.comparePage.outputTokens}</span><input type="number" min="0" max="10000000000" step="100000" value={outputTokens} onChange={(event) => setOutputTokens(clampTokens(event.target.valueAsNumber))} /></label>
            <label><span>{copy.comparePage.cachedTokens}</span><input type="number" min="0" max={inputTokens} step="100000" value={cachedInputTokens} onChange={(event) => setCachedInputTokens(Math.min(inputTokens, clampTokens(event.target.valueAsNumber)))} /></label>
          </div>
          <div className={styles.costResults}>
            {[{ model: first, cost: firstCost }, { model: second, cost: secondCost }].map(({ model, cost }) => (
              <article key={model.slug}>
                <span><ModelMark model={model} compact /> {model.shortName}</span>
                <strong>{cost === null ? copy.detail.unknown : formatUsdPrice(cost, locale)}</strong>
                <small>{copy.comparePage.monthlyEstimate}</small>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.compareSourceLinks}>
        <Link href={`${localizedModelPath(locale, `/ai-model/${first.slug}`)}#sources`}>{copy.comparePage.viewSources}: {first.shortName}</Link>
        <Link href={`${localizedModelPath(locale, `/ai-model/${second.slug}`)}#sources`}>{copy.comparePage.viewSources}: {second.shortName}</Link>
      </div>
    </>
  );
}
